import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import * as Sentry from '@sentry/react';

const monthNames = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

function getMonthName(period) {
    const month = parseInt(period.toString().slice(4, 6), 10);
    const year = period.toString().slice(0, 4);
    return `${monthNames[month]} ${year}`;
}

const AnchorTradingData = ({ period }) => {
    const { apiFetch } = useAuth();
    const [AnchorStats, setAnchorStats] = useState({
        CurrentOccupiedArea: 0,
        CurrentActualTurnover: 0,
        PreviousOccupiedArea: 0,
        PreviousActualTurnover: 0,
        CurrentTenants: [],
        PreviousTenants: []
    });

    useEffect(() => {
        const fetchAnchorStats = async () => {
            try {
                if (period != null) {
                    const Response = await apiFetch(`/api/Mda/AnchorTenantStats?Period=${period}`);
                    const Result = await Response.json();
                    if (Result.success && Result.data) {
                        setAnchorStats({
                            CurrentOccupiedArea: Result.data.currentYear.totalOccupiedArea,
                            CurrentActualTurnover: Result.data.currentYear.actualTurnover,
                            PreviousOccupiedArea: Result.data.previousYear.totalOccupiedArea,
                            PreviousActualTurnover: Result.data.previousYear.actualTurnover,
                            CurrentTenants: Result.data.currentYear.tenants || [],
                            PreviousTenants: Result.data.previousYear.tenants || []
                        });
                    } else {
                        console.error("No data returned");
                    }
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error('Error fetching anchor stats:', e);
            }
        };
        
        fetchAnchorStats();
    }, [period, apiFetch]);


    if (!period) return null;

    // Parse period
    const PeriodInt = parseInt(period, 10);
    const Year = parseInt(period.toString().slice(0, 4), 10);

    // Previous year YTD
    const PrevYear = Year - 1;

    const PrevMonthYear = getMonthName(`${PrevYear}${period.toString().slice(4, 6)}`);

    // Table headers
    const MonthYear = getMonthName(PeriodInt);

    const CurrentDensity = AnchorStats.CurrentOccupiedArea > 0
        ? AnchorStats.CurrentActualTurnover / AnchorStats.CurrentOccupiedArea
        : null;
    const PreviousDensity = AnchorStats.PreviousOccupiedArea > 0
        ? AnchorStats.PreviousActualTurnover / AnchorStats.PreviousOccupiedArea
        : null;

    // Calculate variance as a percentage
    const VariancePercent = (CurrentDensity !== null && PreviousDensity > 0)
        ? ((CurrentDensity - PreviousDensity) / PreviousDensity) * 100
        : null;

    /**
     * HTML-escapes a string to prevent XSS attacks
     * @param {string} Text - The text to escape
     * @returns {string} - HTML-escaped text
     */
    function escapeHtml(Text) {
        if (!Text) return '';
        const Div = document.createElement('div');
        Div.textContent = Text;
        return Div.innerHTML;
    }

    /**
     * Formats a number with spaces as thousand separators and no decimal places.
     * The function rounds to whole numbers and uses spaces instead of commas for thousand separators.
     * @param {number|string} num - The number to format
     * @returns {string} - Formatted whole number with spaces as thousand separators, or 'N/A' for invalid input
     */
    function formatNumberWithWideSpace(Num) {
        // Ensure we have a valid number
        const NumValue = typeof Num === 'string' ? parseFloat(Num) : Num;
        
        // Validate that we have a valid number - return N/A for consistency with the component
        if (isNaN(NumValue) || !isFinite(NumValue)) {
            return 'N/A';
        }
        
        // Format with thousand separators using toLocaleString (no decimal places)
        const Formatted = NumValue.toLocaleString('en-US', { 
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        });
        
        // Replace commas with spaces for the wide space separator style
        return Formatted.replace(/,/g, ' ');
    }

    /**
     * Generates tooltip HTML showing individual anchor tenant trading densities.
     * @param {Array} Tenants - Array of tenant objects with trading density data
     * @returns {string} - HTML string for tooltip display
     */
    function getAnchorTenantTooltipHtml(Tenants) {
        if (!Tenants || Tenants.length === 0) {
            return '<div>No anchor tenant data available</div>';
        }

        const SortedTenants = [...Tenants].sort((a, b) =>
            (a.propertyName || '').localeCompare(b.propertyName || '')
        );

        let Html = `<div style="max-width: 800px; border: 1px solid #ccc; border-radius: 4px; background: white; padding: 8px;">
            <table style="width: 100%; border-collapse: collapse;">
            <thead style="display: table; width: 100%; table-layout: fixed;">
                <tr style="background: #f5f5f5; border-bottom: 2px solid #ddd;">
                    <th style="width: 20%; padding: 8px; text-align: left; font-weight: bold; border-right: 1px solid #ddd;">Property Name</th>
                    <th style="width: 20%; padding: 8px; text-align: left; font-weight: bold; border-right: 1px solid #ddd;">Tenant Name</th>
                    <th style="width: 20%; padding: 8px; text-align: right; font-weight: bold; border-right: 1px solid #ddd;">Occupied Area (m²)</th>
                    <th style="width: 20%; padding: 8px; text-align: right; font-weight: bold; border-right: 1px solid #ddd;">Actual Turnover (R)</th>
                    <th style="width: 20%; padding: 8px; text-align: right; font-weight: bold;">Trading Density (R/m²/month)</th>
                </tr>
            </thead>
            </table>
            <div style="max-height: 300px; overflow-y: auto; display: block; border-bottom: 1px solid #ccc;">
                <table style="width: 100%; border-collapse: collapse;">
                <tbody style="display: table; width: 100%; table-layout: fixed;">`;

        SortedTenants.forEach(Tenant => {
            Html += `<tr style="border-bottom: 1px solid #eee;">
                <td style="width: 20%; padding: 6px 8px; border-right: 1px solid #eee;">${escapeHtml(Tenant.propertyName)}</td>
                <td style="width: 20%; padding: 6px 8px; border-right: 1px solid #eee;">${escapeHtml(Tenant.tenantName)}</td>
                <td style="width: 20%; padding: 6px 8px; text-align: right; border-right: 1px solid #eee;">${formatNumberWithWideSpace(Tenant.occupiedArea)}</td>
                <td style="width: 20%; padding: 6px 8px; text-align: right; border-right: 1px solid #eee;">${formatNumberWithWideSpace(Tenant.actualTurnover)}</td>
                <td style="width: 20%; padding: 6px 8px; text-align: right;">${formatNumberWithWideSpace(Tenant.tradingDensity)}</td>
            </tr>`;
        });

        Html += '</tbody></table></div></div>';
        return Html;
    }

    return (
        <div>
            <table>
                <thead>
                    <tr>
                        <th>Anchor Trading Statistics</th>
                        <th>
                            3 Months to<br />
                            {MonthYear}
                        </th>
                        <th>3 Months to<br />
                            {PrevMonthYear}
                        </th>
                        <th>Growth<br />
                            Year on Year
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Anchor trading density (R/m²/month)</td>
                        <td
                            data-tooltip-id="anchor-tooltip"
                            data-tooltip-content={getAnchorTenantTooltipHtml(AnchorStats.CurrentTenants)}
                        >
                            {AnchorStats.CurrentOccupiedArea > 0
                                ? formatNumberWithWideSpace(
                                    Math.round((AnchorStats.CurrentActualTurnover / AnchorStats.CurrentOccupiedArea) / 3)
                                )
                                : 'N/A'}
                        </td>
                        <td
                            data-tooltip-id="anchor-tooltip"
                            data-tooltip-content={getAnchorTenantTooltipHtml(AnchorStats.PreviousTenants)}
                        >
                            {AnchorStats.PreviousOccupiedArea > 0
                                ? formatNumberWithWideSpace(
                                    Math.round((AnchorStats.PreviousActualTurnover / AnchorStats.PreviousOccupiedArea) / 3)
                                )
                                : 'N/A'}
                        </td>
                        <td>
                            {VariancePercent !== null
                                ? `${VariancePercent.toFixed(2)}%`
                                : 'N/A'}
                        </td>
                    </tr>
                </tbody>
            </table>
            <ReactTooltip
                id="anchor-tooltip"
                place="bottom"
                variant="dark"
                clickable={true}
                delayShow={0}
                delayHide={500}
                offset={5}
                float={false}
                positionStrategy="fixed"
                openEvents={{ mouseenter: true }}
                closeEvents={{ mouseleave: true }}
                globalCloseEvents={{ escape: true, resize: true, clickOutsideAnchor: true }}
                disableTooltip={(anchor) => !anchor?.getAttribute('data-tooltip-content')}
                render={({ content }) =>
                    content ? <div dangerouslySetInnerHTML={{ __html: content }} /> : null
                }
            />
        </div>
    );
};

export default AnchorTradingData;